import { PoolDataCache } from "../utils/poolDataCache";
import React, {
  useCallback,
  createContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from "react";
import { current, Draft, produce } from "immer";
import { useImmer } from "use-immer";
import { Mutex } from "async-mutex";
import { AccountInfo, DAppClient, NetworkType } from "@airgap/beacon-dapp";
import {
  Transaction,
  Asset,
  AssetBalance,
  Balance,
  TransactionStatus,
  TransactingComponent,
  Amount,
  AssetOrAssetPair,
  CompletionState,
  Token,
} from "../types/general";
import { eq, isNumber } from "lodash";
import { processTransaction } from "../functions/transactions";
import { useNetwork } from "../hooks/network";
import { useSession } from "../hooks/session";
import { v4 as uuidv4 } from "uuid";
import { BigNumber } from "bignumber.js";
import {
  createDAppClient,
  createCustomDAppClient,
  disposeDAppClient,
  getBalance,
  subscribeToActiveAccount,
} from "../functions/beacon";
import {
  completionRecordFailed,
  completionRecordSuccess,
} from "../functions/util";
import { WritableDraft } from "immer/dist/types/types-external";
import { estimateWithAdapter } from "../functions/estimates";
import {
  applyQuoteResult,
  createQuoteGuardedDAppClient,
  createQuoteRequest,
  createTransactionQuote,
  hasFreshTransactionQuote,
  QuoteContext,
  QuoteRequest,
  quoteRequestMatches,
  transactionResultMatchesActive,
} from "../functions/quoteSafety";
import {
  getSlippageValidationMessage,
  getStatusAfterBalanceCheck,
  isValidSlippage,
  isTransactionInProgress,
  shouldApplySlippageUpdate,
  shouldApplyTransactionStatus,
  XTZ_FEE_RESERVE_TEZ,
} from "../functions/transactionSafety";
import {
  accountMatchesConfiguredNetwork,
  ConfiguredNetworkIdentity,
  createConfiguredNetworkIdentity,
  createGuardedDAppClient,
  createOperationRequestPolicy,
  networkIdentityFingerprint,
  WalletIdentityError,
} from "../functions/walletIdentity";
import { isStezOnlyHost } from "../routing";
import { SNET_CHAIN_ID, SNET_NETWORK, SNET_RPC_URL } from "../config/snet";

export enum WalletStatus {
  ESTIMATING_SIRS = "Estimating Sirs",
  ESTIMATING_XTZ = "Estimating Tez",
  ESTIMATING_TZBTC = "Estimating tzBTC",
  ZERO_BALANCE = "Insufficient Funds",
  ZERO_AMOUNT = "Enter Amount",
  DISCONNECTED = "disconnected",
  READY = "ready",
  BUSY = "In Progress",
  LOADING = "Loading",
}

export interface WalletInfo {
  client: DAppClient | null;
  address: string | null;
  syncActiveAccount: (
    sourceClient: DAppClient,
    account: AccountInfo | undefined,
    expectedNetwork?: ConfiguredNetworkIdentity
  ) => boolean;
  isWalletConnected: boolean;
  disconnect: () => Promise<void>;
  initialiseTransaction: (
    component: TransactingComponent,
    sendAsset: AssetOrAssetPair,
    receiveAsset: AssetOrAssetPair,
    poolId: string,
    sendAmount?: Amount,
    receiveAmount?: Amount,
    slipppage?: number
  ) => Promise<boolean>;
  updateTransactionBalance: (
    component: TransactingComponent
  ) => Promise<boolean>;
  updateStatus: (
    component: TransactingComponent,
    transactionStatus: TransactionStatus
  ) => Promise<void>;
  updateAmount: (
    component: TransactingComponent,
    amountUpdateSend?: Amount,
    amountUpdateReceive?: Amount,
    slippageUpdate?: number
  ) => Promise<boolean>;
  refreshTransactionQuote: (
    component: TransactingComponent,
    amountUpdateSend?: Amount,
    slippageUpdate?: number,
    debounceMs?: number
  ) => Promise<boolean>;
  prepareTransactionForSubmission: (
    component: TransactingComponent
  ) => Promise<boolean>;
  invalidateTransactionQuote: (
    component: TransactingComponent,
    transactionStatus: TransactionStatus,
    slippageInput?: string
  ) => Promise<void>;
  getActiveTransaction: (
    component: TransactingComponent
  ) => Transaction | undefined;
  clearTransaction: (component: TransactingComponent) => void;
}

const defaultWalletInfo: WalletInfo = {
  client: null,
  address: null,
  syncActiveAccount: () => {
    throw new Error("syncActiveAccount called outside of wallet provider");
  },
  isWalletConnected: false,
  disconnect: () => {
    throw new Error("disconnect called outside of wallet provider");
  },
  initialiseTransaction: async () => {
    throw new Error("initialiseTransaction called outside of wallet provider");
  },
  updateTransactionBalance: async () => {
    throw new Error(
      "updateTransactionBalance called outside of wallet provider"
    );
  },
  updateStatus: async () => {
    throw new Error("updateStatus called outside of wallet provider");
  },
  updateAmount: async () => {
    throw new Error("updateAmount  called outside of wallet provider");
  },
  refreshTransactionQuote: async () => {
    throw new Error(
      "refreshTransactionQuote called outside of wallet provider"
    );
  },
  prepareTransactionForSubmission: async () => {
    throw new Error(
      "prepareTransactionForSubmission called outside of wallet provider"
    );
  },
  invalidateTransactionQuote: async () => {
    throw new Error(
      "invalidateTransactionQuote called outside of wallet provider"
    );
  },
  getActiveTransaction: () => {
    throw new Error("getActiveTransaction called outside of wallet provider");
  },
  clearTransaction: () => {
    throw new Error("clearTransaction called outside of wallet provider");
  },
};

export const WalletContext = createContext<WalletInfo>(defaultWalletInfo);

export interface IWallet {
  children:
    | JSX.Element[]
    | JSX.Element
    | React.ReactElement
    | React.ReactElement[]
    | string;
}

interface IWalletProvider {
  children:
    | JSX.Element[]
    | JSX.Element
    | React.ReactElement
    | React.ReactElement[]
    | string;
}
export function WalletProvider(props: IWalletProvider) {
  const network = useNetwork();
  const session = useSession();
  const transactionMutex = useRef(new Mutex()).current;
  const transactionUpdateMutex = useRef(new Mutex()).current;
  const processingTransactionIds = useRef(new Set<string>()).current;
  const latestQuoteRequests = useRef(
    new Map<TransactingComponent, QuoteRequest>()
  ).current;
  const latestTransactionInitializations = useRef(
    new Map<TransactingComponent, symbol>()
  ).current;
  type TransactionState = {
    [key in TransactingComponent]?: Transaction;
  };
  type TransactionStateUpdate =
    | TransactionState
    | ((draft: Draft<TransactionState>) => void);
  const [transactions, setTransactionState] = useImmer<TransactionState>({});
  const transactionsRef = useRef<TransactionState>({});
  const setTransactions = useCallback(
    (update: TransactionStateUpdate): void => {
      const nextTransactions =
        typeof update === "function"
          ? produce(transactionsRef.current, update)
          : update;
      transactionsRef.current = nextTransactions;
      setTransactionState(nextTransactions);
    },
    []
  );

  const [isWalletConnected, setIsWalletConnected] = useState(false);
  const [client, setClient] = useState<DAppClient | null>(null);
  const [address, setAddress] = useState<string | null>(null);
  const hasReconnectedRef = useRef(false);
  const quoteContextRef = useRef<QuoteContext>({
    account: address,
    network: network.network,
    chainId: network.info.chainId,
  });
  quoteContextRef.current = {
    account: address,
    network: network.network,
    chainId: network.info.chainId,
  };
  const networkRef = useRef(network);
  networkRef.current = network;

  const [assetBalances, setAssetBalances] = useState<AssetBalance[]>(
    network.info.assets.map((asset) => {
      return { balance: undefined, asset: asset };
    })
  );
  const configuredNetworkIdentity = useMemo(
    () =>
      createConfiguredNetworkIdentity({
        type: network.network,
        chainId: network.info.chainId,
        primaryRpcUrl: network.info.tezosServer,
        fallbackRpcUrls: network.info.rpcFallbacks,
      }),
    [
      network.info.chainId,
      network.info.rpcFallbacks,
      network.info.tezosServer,
      network.network,
    ]
  );
  const configuredNetworkFingerprint = networkIdentityFingerprint(
    configuredNetworkIdentity
  );
  const addressRef = useRef<string | null>(null);
  const activeAccountIdentifierRef = useRef<string | null>(null);
  const connectionRevisionRef = useRef(0);

  const clearWalletDerivedState = useCallback(() => {
    processingTransactionIds.clear();
    latestQuoteRequests.clear();
    latestTransactionInitializations.clear();
    setTransactions({});
    setAssetBalances(
      networkRef.current.info.assets.map((asset) => ({
        balance: undefined,
        asset,
      }))
    );
  }, [
    latestQuoteRequests,
    latestTransactionInitializations,
    processingTransactionIds,
    setTransactions,
  ]);

  const syncActiveAccount = useCallback(
    (
      sourceClient: DAppClient,
      account: AccountInfo | undefined,
      expectedNetwork?: ConfiguredNetworkIdentity
    ): boolean => {
      connectionRevisionRef.current += 1;
      clearWalletDerivedState();

      const acceptedNetwork =
        expectedNetwork ??
        createConfiguredNetworkIdentity({
          type: networkRef.current.network,
          chainId: networkRef.current.info.chainId,
          primaryRpcUrl: networkRef.current.info.tezosServer,
          fallbackRpcUrls: networkRef.current.info.rpcFallbacks,
        });

      if (
        !account ||
        !accountMatchesConfiguredNetwork(account, acceptedNetwork)
      ) {
        addressRef.current = null;
        activeAccountIdentifierRef.current = null;
        setAddress(null);
        setClient(null);
        return false;
      }

      addressRef.current = account.address;
      activeAccountIdentifierRef.current = account.accountIdentifier;
      setAddress(account.address);
      setClient(sourceClient);
      return true;
    },
    [clearWalletDerivedState]
  );
  const syncActiveAccountRef = useRef(syncActiveAccount);
  syncActiveAccountRef.current = syncActiveAccount;
  const initialNetworkRef = useRef(network);

  // zero balance object
  const zeroBalance: Balance = {
    decimal: new BigNumber(0),
    mantissa: new BigNumber(0),
    string: "0.00",
    greaterOrEqualTo: (balance: Balance): boolean => {
      return new BigNumber(0).isGreaterThanOrEqualTo(balance.mantissa);
    },
  };

  //calback to find the balance of an asset
  const findAssetBalance = useCallback(
    (asset: Asset): Balance => {
      // find the balance of an asset
      const found = assetBalances.find((assetBalance: AssetBalance) => {
        return (assetBalance.asset.name as string) === (asset.name as string);
      });
      // if found return the balance or zero balance else throw error
      if (found) {
        if (found.balance) return found.balance as Balance;
        // if balance is undefined return zero balance
        return zeroBalance;
      }
      // Stale transactions can still reference assets from a previous network
      // (e.g. TzBTC after switching Mainnet → Previewnet). Treat as zero.
      return zeroBalance;
    },
    [assetBalances]
  );

  // callback to get the balance of an asset or an asset pair
  const getBalancesOfAssets = useCallback(
    (assets: AssetOrAssetPair): Amount => {
      // map asset or asset pair to get combined amount
      const amount: Amount = assets.map((asset: Asset) => {
        // find the balance of this asset
        return findAssetBalance(asset);
      }) as Amount;
      return amount;
    },
    [findAssetBalance]
  );

  // Auto-reconnect on mount (ONCE)
  useEffect(() => {
    const autoReconnect = async () => {
      if (hasReconnectedRef.current) {
        return;
      }

      hasReconnectedRef.current = true;

      try {
        const isStezSurface =
          isStezOnlyHost(window.location.hostname) ||
          window.location.pathname.startsWith("/stez");
        const expectedNetwork = isStezSurface
          ? createConfiguredNetworkIdentity({
              type: NetworkType.CUSTOM,
              chainId: SNET_CHAIN_ID,
              primaryRpcUrl: SNET_RPC_URL,
            })
          : undefined;
        const dAppClient = isStezSurface
          ? createCustomDAppClient({
              name: SNET_NETWORK.name,
              rpcUrl: SNET_RPC_URL,
              chainId: SNET_CHAIN_ID,
            })
          : createDAppClient(initialNetworkRef.current);

        await subscribeToActiveAccount(dAppClient, (account) => {
          const accepted = syncActiveAccountRef.current(
            dAppClient,
            account,
            expectedNetwork
          );
          if (account && !accepted) {
            void disposeDAppClient(dAppClient).catch((cleanupError) =>
              console.warn(
                "Could not clean up the rejected Beacon session:",
                cleanupError
              )
            );
          }
        });

        const activeAccount = await dAppClient.getActiveAccount();

        if (!activeAccount) {
          await disposeDAppClient(dAppClient);
          return;
        }

        if (
          !syncActiveAccountRef.current(
            dAppClient,
            activeAccount,
            expectedNetwork
          )
        ) {
          await disposeDAppClient(dAppClient);
        }
      } catch (error) {
        console.error("Auto-reconnect failed:", error);
      }
    };

    autoReconnect();
  }, []);

  const balanceRefreshRevisionRef = useRef<number | undefined>(undefined);
  // Publish each asset as it arrives: an unavailable LP view must not hide
  // the XTZ/token balances required by the currently visible form.
  const updateBalances = useCallback(async () => {
    if (!address || !client) return;
    const revision = connectionRevisionRef.current;
    if (balanceRefreshRevisionRef.current === revision) return;
    balanceRefreshRevisionRef.current = revision;
    try {
      await Promise.allSettled(
        network.info.assets.map(async (asset) => {
          try {
            const balance = await getBalance(
              network.toolkit,
              network.network,
              address,
              asset
            );
            if (connectionRevisionRef.current !== revision) return;
            setAssetBalances((previous) =>
              previous.map((entry) =>
                entry.asset.name === asset.name &&
                !entry.balance?.mantissa.eq(balance.mantissa)
                  ? { asset, balance }
                  : entry
              )
            );
          } catch (error) {
            // Keep other successful reads and retry this asset on the next poll.
            console.warn(
              "Could not refresh balance for " + asset.name + ":",
              error
            );
          }
        })
      );
    } finally {
      if (balanceRefreshRevisionRef.current === revision)
        balanceRefreshRevisionRef.current = undefined;
    }
  }, [address, client, network.toolkit, network.network, network.info.assets]);

  useEffect(() => {
    void updateBalances();
  }, [updateBalances]);

  // Track the full canonical identity so two CUSTOM networks never compare equal.
  const previousNetworkRef = useRef(configuredNetworkFingerprint);

  // Disconnect wallet when network changes
  useEffect(() => {
    const handleNetworkSwitch = async () => {
      if (previousNetworkRef.current !== configuredNetworkFingerprint) {
        previousNetworkRef.current = configuredNetworkFingerprint;
        connectionRevisionRef.current += 1;
        addressRef.current = null;
        activeAccountIdentifierRef.current = null;
        clearWalletDerivedState();
        setAddress(null);
        setClient(null);

        if (client) {
          try {
            await disposeDAppClient(client);
          } catch (error) {
            console.error("Error clearing Beacon account:", error);
          }
        }
      }
    };

    handleNetworkSwitch();
  }, [client, clearWalletDerivedState, configuredNetworkFingerprint]);

  useEffect(() => {
    const interval = setInterval(() => {
      updateBalances();
    }, 15000);

    return () => clearInterval(interval);
  }, [updateBalances]);

  // callback to return the active transaction of a component
  const getActiveTransaction = useCallback(
    (component: TransactingComponent): Transaction | undefined => {
      return transactionsRef.current[component];
    },
    []
  );

  const clearTransaction = useCallback(
    (component: TransactingComponent) => {
      if (isTransactionInProgress(transactionsRef.current[component])) return;
      latestQuoteRequests.delete(component);
      latestTransactionInitializations.delete(component);
      setTransactions((draft) => {
        delete draft[component];
      });
    },
    [latestQuoteRequests, latestTransactionInitializations, setTransactions]
  );

  // calback to send a transaction for final processing
  const transact = useCallback(
    async (transaction: Transaction): Promise<Transaction> => {
      // if the wallet is connected and the toolkit is defined
      if (networkRef.current.toolkit && client) {
        // if the transaction is pending process it
        if (transaction.transactionStatus === TransactionStatus.PENDING) {
          let submittedHash: string | undefined;

          try {
            if (
              !hasFreshTransactionQuote(
                transaction,
                quoteContextRef.current,
                true
              )
            ) {
              throw new Error(
                "Transaction quote changed before wallet submission"
              );
            }

            const submission = transaction.submissionContext;
            if (!submission) {
              throw new WalletIdentityError(
                "This transaction was not prepared for the active wallet session."
              );
            }

            const activeNetwork = networkRef.current;
            const adapter = activeNetwork.getPoolAdapter(transaction.poolId);
            if (adapter.poolConfig.address !== submission.recipient) {
              throw new WalletIdentityError(
                "The active pool recipient changed after this transaction was prepared."
              );
            }

            const operationPolicy = createOperationRequestPolicy(
              transaction,
              adapter.poolConfig
            );
            const quoteGuardedClient = createQuoteGuardedDAppClient({
              client,
              transaction,
              getActiveTransaction: () =>
                transactionsRef.current[transaction.component],
              getRuntime: () => {
                const currentNetwork = networkRef.current;
                return {
                  context: { ...quoteContextRef.current },
                  readChainId: () => currentNetwork.toolkit.rpc.getChainId(),
                };
              },
            });
            const guardedClient = createGuardedDAppClient({
              client: quoteGuardedClient,
              submission,
              transactionNetwork: transaction.network,
              operationPolicy,
              getRuntime: () => {
                const currentNetwork = networkRef.current;
                return {
                  address: addressRef.current,
                  accountIdentifier: activeAccountIdentifierRef.current,
                  connectionRevision: connectionRevisionRef.current,
                  network: createConfiguredNetworkIdentity({
                    type: currentNetwork.network,
                    chainId: currentNetwork.info.chainId,
                    primaryRpcUrl: currentNetwork.info.tezosServer,
                    fallbackRpcUrls: currentNetwork.info.rpcFallbacks,
                  }),
                  rpcUrl: currentNetwork.toolkit.rpc.getRpcUrl(),
                  readChainId: () => currentNetwork.toolkit.rpc.getChainId(),
                };
              },
            });

            const success = await processTransaction(
              transaction,
              submission.owner,
              { toolkit: activeNetwork.toolkit, client: guardedClient },
              {
                onSubmitted: (opHash) => {
                  submittedHash = opHash;
                  setTransactions((draft) => {
                    const activeTransaction = draft[transaction.component];
                    if (activeTransaction?.id !== transaction.id) return;

                    activeTransaction.operationHash = opHash;
                    activeTransaction.transactionStatus =
                      TransactionStatus.SUBMITTED;
                    activeTransaction.lastModified = new Date();
                  });
                },
              }
            );

            session.setAlert(completionRecordSuccess(success), true);
            try {
              await updateBalances();
            } catch (balanceError) {
              // Confirmation is authoritative. A secondary balance refresh
              // must never turn a confirmed operation into a retryable failure.
              console.warn(
                "Post-confirmation balance refresh failed:",
                balanceError
              );
            }

            return {
              ...transaction,
              operationHash: success.opHash,
              transactionStatus: TransactionStatus.COMPLETED,
            };
          } catch (error: unknown) {
            if (error instanceof WalletIdentityError) {
              connectionRevisionRef.current += 1;
              addressRef.current = null;
              activeAccountIdentifierRef.current = null;
              clearWalletDerivedState();
              setAddress(null);
              setClient(null);
              void disposeDAppClient(client).catch((cleanupError) =>
                console.warn(
                  "Could not clean up the stale Beacon session:",
                  cleanupError
                )
              );
            }
            const completionRecord = completionRecordFailed(
              error,
              transaction.component,
              transaction.network
            );
            session.setAlert(completionRecord, true);

            const failure =
              completionRecord[0] === CompletionState.FAILED
                ? completionRecord[1]
                : undefined;
            const confirmationUnknown =
              failure?.submitted && failure.safeToRetry === false;

            return {
              ...transaction,
              operationHash: failure?.opHash ?? submittedHash,
              transactionStatus: confirmationUnknown
                ? TransactionStatus.CONFIRMATION_UNKNOWN
                : TransactionStatus.FAILED,
            };
          }
        } else {
          return transaction;
        }
      } else throw Error("wallet not Connected");
    },
    [clearWalletDerivedState, client, session, setTransactions, updateBalances]
  );

  // Effect to monitor transactions and send them for processing
  useEffect(() => {
    processingTransactionIds.forEach((transactionId) => {
      const activeTransaction = Object.values(transactions).find(
        (transaction) => transaction?.id === transactionId
      );

      if (
        !activeTransaction ||
        (activeTransaction.transactionStatus !== TransactionStatus.PENDING &&
          activeTransaction.transactionStatus !== TransactionStatus.SUBMITTED)
      ) {
        processingTransactionIds.delete(transactionId);
      }
    });

    // process a transaction
    const proc = async (component: TransactingComponent) => {
      return transactionMutex.runExclusive(async () => {
        const currentTransaction = transactions[component];
        // if there is a transaction and it is pending and not locked
        if (
          currentTransaction &&
          currentTransaction.transactionStatus === TransactionStatus.PENDING &&
          !currentTransaction.locked &&
          !processingTransactionIds.has(currentTransaction.id)
        ) {
          // React can re-run effects with a stale transaction snapshot. Keep an
          // attempt-level guard so the same transaction cannot be submitted twice.
          processingTransactionIds.add(currentTransaction.id);
          // lock transaction
          setTransactions((draft) => {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            draft[component]!.locked = true;
          });
          // send transaction for final processing
          const updatedTransaction = await transact(currentTransaction).then(
            (transaction) => {
              // unlock transaction copy
              transaction.locked = false;
              return transaction;
            }
          );
          setTransactions((draft) => {
            if (
              transactionResultMatchesActive(
                draft[component],
                updatedTransaction
              )
            ) {
              draft[component] = updatedTransaction;
            }
          });
        }
      });
    };

    // process all pending transactions
    Promise.all([
      proc(TransactingComponent.SWAP),
      proc(TransactingComponent.ADD_LIQUIDITY),
      proc(TransactingComponent.REMOVE_LIQUIDITY),
    ]).then(() => {
      //
    });
  }, [transact, transactions, setTransactions]);

  // callback to set the active transaction of a component or apply an operation to the transaction
  const setActiveTransaction = useCallback(
    (
      component: TransactingComponent,
      transaction?: Transaction,
      op?: (transaction: Draft<Transaction>) => boolean
    ): boolean => {
      let updated = false;
      // get the active transaction of the component
      const activeTransaction = getActiveTransaction(component);
      setTransactions((draft) => {
        const draftTransaction = draft[component];
        if (op && draftTransaction) {
          // if there is an operation apply it to the transaction
          updated = op(draftTransaction as Draft<Transaction>);
        } else if (
          transaction &&
          !eq(JSON.stringify(activeTransaction), JSON.stringify(transaction))
        ) {
          draft[component] = transaction;
          updated = true;
        }
      });
      return updated;
    },
    [setTransactions]
  );

  // callback to initialise a transaction
  const initialiseTransaction = useCallback(
    async (
      component: TransactingComponent,
      sendAsset: AssetOrAssetPair,
      receiveAsset: AssetOrAssetPair,
      poolId: string,
      sendAmount?: Amount,
      receiveAmount?: Amount,
      slipppage = 0.5
    ): Promise<boolean> => {
      if (isTransactionInProgress(transactionsRef.current[component]))
        return false;
      const initializationToken = Symbol("transaction initialization");
      latestQuoteRequests.delete(component);
      latestTransactionInitializations.set(component, initializationToken);
      // Publish the new identity before fetching. RPC latency must never hold
      // the mutation lock shared by every trading form.
      {
        const quoteContext = { ...quoteContextRef.current };
        const activeNetwork = networkRef.current;
        const adapter = activeNetwork.getPoolAdapter(poolId);
        const submissionNetwork = createConfiguredNetworkIdentity({
          type: activeNetwork.network,
          chainId: activeNetwork.info.chainId,
          primaryRpcUrl: activeNetwork.info.tezosServer,
          fallbackRpcUrls: activeNetwork.info.rpcFallbacks,
        });
        // initialise zeroBalance
        const initBalance = (asset: AssetOrAssetPair): Amount => {
          switch (asset.length) {
            case 1:
              return [zeroBalance];
            case 2:
              return [zeroBalance, zeroBalance];
          }
        };
        // if no send or recieve amount is provided initialise them to zero balance
        const send: Amount = sendAmount ? sendAmount : initBalance(sendAsset);
        const receive: Amount = receiveAmount
          ? receiveAmount
          : initBalance(receiveAsset);

        const _transaction: Transaction = {
          id: uuidv4(),

          network: activeNetwork.network,
          component,
          poolId,
          sendAsset,
          sendAmount:
            component === TransactingComponent.ADD_LIQUIDITY && send[1]
              ? [send[0], zeroBalance]
              : send,
          sendAssetBalance: initBalance(sendAsset),
          receiveAsset,
          receiveAmount: receive,
          receiveAssetBalance: initBalance(receiveAsset),
          transactionStatus: TransactionStatus.INITIALIZED,
          slippage: slipppage,
          submissionContext: {
            owner: addressRef.current ?? "",
            accountIdentifier: activeAccountIdentifierRef.current ?? "",
            recipient: adapter.poolConfig.address,
            networkType: submissionNetwork.type,
            chainId: submissionNetwork.chainId,
            rpcUrl: submissionNetwork.primaryRpcUrl,
            connectionRevision: connectionRevisionRef.current,
          },
          lastModified: new Date(),
          locked: false,
          quoteRevision: 0,
        };

        const empty = _transaction.sendAmount[0].mantissa.isZero();
        setActiveTransaction(component, {
          ..._transaction,
          transactionStatus: empty
            ? TransactionStatus.ZERO_AMOUNT
            : TransactionStatus.MODIFIED,
        });
        if (empty) {
          // Start the selected pool read while the user chooses an amount. A
          // failed preload is retried by the normal, visible quote request.
          void adapter
            .getPoolData(activeNetwork.toolkit)
            .catch(() => undefined);
          latestTransactionInitializations.delete(component);
          return true;
        }
        const isCurrent = () => {
          const context = quoteContextRef.current;
          return (
            latestTransactionInitializations.get(component) ===
              initializationToken &&
            transactionsRef.current[component]?.id === _transaction.id &&
            quoteContext.account === context.account &&
            quoteContext.network === context.network &&
            quoteContext.chainId === context.chainId
          );
        };
        try {
          const estimated = await estimateWithAdapter(
            _transaction,
            activeNetwork.toolkit,
            adapter
          );
          if (!isCurrent()) return false;
          const active = transactionsRef.current[component];
          if (!active) return false;
          const quoted = {
            ...estimated,
            sendAssetBalance: active.sendAssetBalance,
            receiveAssetBalance: active.receiveAssetBalance,
            quoteRevision: 1,
            hasReceivedQuote: true,
          };
          latestTransactionInitializations.delete(component);
          return setActiveTransaction(component, {
            ...quoted,
            quote: createTransactionQuote(quoted, quoteContext, 1),
          });
        } catch (error) {
          if (isCurrent()) {
            latestTransactionInitializations.delete(component);
            setTransactions((draft) => {
              const active = draft[component];
              if (active?.id === _transaction.id)
                active.transactionStatus = TransactionStatus.FAILED;
            });
          }
          return false;
        }
      }
    },
    [setActiveTransaction, setTransactions]
  );

  // function to check if a user has sufficient balance
  const checkSufficientBalance = useCallback(
    (
      userBalance: Amount,
      requiredAmount: Amount,
      requiredAssets: AssetOrAssetPair
    ): TransactionStatus => {
      // Amounts to compare must be of same length( currently : a single asset or an asset pair)
      if (userBalance.length !== requiredAmount.length) {
        throw Error("Error: balance check asset pair mismatch");
      }
      // check if the user has sufficient balance by comparing the two amounts
      const checks: boolean[] = Array.from(
        userBalance,
        (assetBalance, index) => {
          const required = requiredAmount[index];
          if (required) {
            return assetBalance.greaterOrEqualTo(required);
          } else throw Error("Amount indexs don't match / align");
        }
      );
      const xtzAsset = network.info.assets.find(
        (asset) => asset.name === Token.XTZ
      );
      const requiredXtz = requiredAssets.reduce((total, asset, index) => {
        if (asset.name !== Token.XTZ) return total;
        return total.plus(requiredAmount[index]?.decimal ?? 0);
      }, new BigNumber(0));
      const hasFeeReserve = xtzAsset
        ? findAssetBalance(xtzAsset).decimal.isGreaterThanOrEqualTo(
            requiredXtz.plus(XTZ_FEE_RESERVE_TEZ)
          )
        : false;

      // Every operation is paid in tez, including token-only swaps. Require a
      // small fee reserve in addition to the assets sent to the pool.
      const hasSufficientBalance = !checks.includes(false) && hasFeeReserve;
      if (hasSufficientBalance) {
        return TransactionStatus.SUFFICIENT_BALANCE;
      } else {
        return TransactionStatus.INSUFFICIENT_BALANCE;
      }
    },
    [findAssetBalance, network.info.assets]
  );

  // callback to update balances and status of a transaction balance for a given transaction
  const TranscationWithUpdatedBalance = useCallback(
    (transaction: Transaction): Transaction => {
      const sendAssetBalance: Amount = getBalancesOfAssets(
        transaction.sendAsset
      );
      const receiveAssetBalance: Amount = getBalancesOfAssets(
        transaction.receiveAsset
      );
      const balanceStatus = checkSufficientBalance(
        sendAssetBalance,
        transaction.sendAmount,
        transaction.sendAsset
      );
      return {
        ...transaction,
        sendAssetBalance,
        receiveAssetBalance,
        transactionStatus: getStatusAfterBalanceCheck(
          transaction.transactionStatus,
          balanceStatus
        ),
      };
    },
    [checkSufficientBalance, getBalancesOfAssets]
  );

  // callback to update balances of all transactions
  const updateBalancesOfAllTransactions = useCallback(async () => {
    await transactionUpdateMutex.runExclusive(() => {
      // iterate over all transactions
      Object.entries(transactionsRef.current).forEach(([key, transaction]) => {
        // key is the component
        const component = key as TransactingComponent;
        // if the transaction is defined
        if (transaction) {
          // get the new transaction with updated balances
          const updatedTransaction = TranscationWithUpdatedBalance(transaction);

          // old transaction before balance update
          const t = transaction;
          // if the new transaction is different from the old one update the state
          if (t && !eq(JSON.stringify(updatedTransaction), JSON.stringify(t))) {
            setTransactions((draft) => {
              updateTransaction(draft[component], (transaction) => {
                if (
                  transaction.sendAssetBalance !==
                  updatedTransaction.sendAssetBalance
                ) {
                  transaction.lastModified = new Date();
                  transaction.sendAssetBalance =
                    updatedTransaction.sendAssetBalance;
                }
                if (
                  transaction.receiveAssetBalance !==
                  updatedTransaction.receiveAssetBalance
                ) {
                  transaction.lastModified = new Date();

                  transaction.receiveAssetBalance =
                    updatedTransaction.receiveAssetBalance;
                }
                if (
                  transaction.transactionStatus !==
                  updatedTransaction.transactionStatus
                ) {
                  transaction.lastModified = new Date();

                  transaction.transactionStatus =
                    updatedTransaction.transactionStatus;
                }

                return true;
              });
            });
          }
        }
      });
    });
  }, [transactions, TranscationWithUpdatedBalance, setTransactions]);

  // Effect to update balances of all transactions on change of balances
  useEffect(() => {
    updateBalancesOfAllTransactions();
  }, [assetBalances, updateBalancesOfAllTransactions]);

  // exported callback to update transaction balance of a  single component
  const updateTransactionBalance = useCallback(
    async (component: TransactingComponent): Promise<boolean> => {
      let updated = false;
      await transactionUpdateMutex.runExclusive(() => {
        // Read after acquiring the lock: an amount edit may have been queued
        // ahead of this refresh and already invalidated the previous quote.
        const transaction = transactionsRef.current[component];
        if (!transaction) return;
        const refreshed = TranscationWithUpdatedBalance(transaction);
        setTransactions((draft) => {
          updated = updateTransaction(draft[component], (active) => {
            if (active.id !== transaction.id) return false;
            active.sendAssetBalance = refreshed.sendAssetBalance;
            active.receiveAssetBalance = refreshed.receiveAssetBalance;
            active.transactionStatus = refreshed.transactionStatus;
            active.lastModified = new Date();
            return true;
          });
        });
      });
      return updated;
    },
    [TranscationWithUpdatedBalance, setTransactions]
  );

  // exported callback to update transaction status of a  single component
  const updateStatus = useCallback(
    async (
      component: TransactingComponent,
      transactionStatus: TransactionStatus
    ) => {
      await transactionUpdateMutex.runExclusive(() => {
        setTransactions((draft) => {
          updateTransaction(
            draft[component],
            // update function
            (transaction) => {
              if (
                !shouldApplyTransactionStatus(
                  transaction.transactionStatus,
                  transactionStatus
                )
              ) {
                return false;
              }

              transaction.transactionStatus = transactionStatus;
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              draft[component]!.lastModified = new Date();
              return true;
            }
          );
        });
      });
    },
    [setTransactions]
  );

  // update transaction status based on balance for a single draft transaction
  // for use with update amount function
  const updateTransactionStatusBasedOnBalance = (
    transaction: WritableDraft<Transaction>,
    amountUpdateSend?: Amount
  ) => {
    const sendAssetBalance: Amount = transaction.sendAssetBalance.map(
      (balance) => ({ ...balance })
    ) as Amount;
    // if the send amount has changed update the transaction status and balance
    if (
      amountUpdateSend &&
      !transaction.sendAmount[0].decimal.eq(amountUpdateSend[0].decimal)
    ) {
      transaction.sendAmount = amountUpdateSend;
      if (isWalletConnected) {
        transaction.transactionStatus = getStatusAfterBalanceCheck(
          transaction.transactionStatus,
          checkSufficientBalance(
            sendAssetBalance,
            amountUpdateSend,
            transaction.sendAsset as AssetOrAssetPair
          )
        );
      }
      return true;
    }
    return false;
  };

  // Allows for safe update of a transaction draft
  const updateTransaction = (
    transaction: WritableDraft<Transaction> | undefined,
    updater: (transaction: WritableDraft<Transaction>) => boolean
  ): boolean => {
    // if the transaction is locked or undefined  return
    if (!transaction) return false;
    if (
      transaction.locked ||
      transaction.transactionStatus === TransactionStatus.PENDING ||
      transaction.transactionStatus === TransactionStatus.SUBMITTED ||
      transaction.transactionStatus ===
        TransactionStatus.CONFIRMATION_UNKNOWN ||
      transaction.transactionStatus === TransactionStatus.COMPLETED
    )
      return false;
    // else safe to update, run the updater

    return updater(transaction);
  };

  const invalidateTransactionQuote = useCallback(
    async (
      component: TransactingComponent,
      transactionStatus: TransactionStatus,
      slippageInput?: string
    ): Promise<void> => {
      const expectedId = transactionsRef.current[component]?.id;
      latestQuoteRequests.delete(component);
      latestTransactionInitializations.delete(component);
      await transactionUpdateMutex.runExclusive(() => {
        setTransactions((draft) => {
          updateTransaction(draft[component], (transaction) => {
            if (transaction.id !== expectedId) return false;
            if (slippageInput !== undefined)
              transaction.slippageInput = slippageInput;
            transaction.quoteRevision = (transaction.quoteRevision ?? 0) + 1;
            latestQuoteRequests.delete(component);
            delete transaction.quote;
            if (
              component === TransactingComponent.ADD_LIQUIDITY &&
              transaction.sendAmount[1]
            )
              transaction.sendAmount[1] = zeroBalance;
            transaction.receiveAmount = transaction.receiveAsset.map(
              () => zeroBalance
            ) as Amount;
            transaction.transactionStatus = transactionStatus;
            transaction.lastModified = new Date();
            return true;
          });
        });
      });
    },
    [latestQuoteRequests, setTransactions, transactionUpdateMutex]
  );

  const runTransactionQuote = useCallback(
    async (
      component: TransactingComponent,
      amountUpdateSend?: Amount,
      slippageUpdate?: number,
      submitAfterRefresh = false,
      debounceMs = 0
    ): Promise<boolean> => {
      latestQuoteRequests.delete(component);
      latestTransactionInitializations.delete(component);
      const expectedTransactionId = transactionsRef.current[component]?.id;
      let pending:
        | { request: QuoteRequest; transaction: Transaction }
        | undefined;
      let updated = false;

      await transactionUpdateMutex.runExclusive(() => {
        setTransactions((draft) => {
          updated = updateTransaction(draft[component], (transaction) => {
            if (transaction.id !== expectedTransactionId) return false;
            latestQuoteRequests.delete(component);
            if (amountUpdateSend) transaction.sendAmount = amountUpdateSend;
            // The second deposit is a quote result, not an independent input.
            if (
              component === TransactingComponent.ADD_LIQUIDITY &&
              transaction.sendAmount[1]
            )
              transaction.sendAmount[1] = zeroBalance;
            if (isNumber(slippageUpdate) && isValidSlippage(slippageUpdate))
              delete transaction.slippageInput;
            if (
              (isNumber(slippageUpdate) && !isValidSlippage(slippageUpdate)) ||
              (transaction.slippageInput !== undefined &&
                getSlippageValidationMessage(transaction.slippageInput))
            ) {
              transaction.quoteRevision = (transaction.quoteRevision ?? 0) + 1;
              delete transaction.quote;
              transaction.receiveAmount = transaction.receiveAsset.map(
                () => zeroBalance
              ) as Amount;
              transaction.transactionStatus =
                TransactionStatus.INVALID_SLIPPAGE;
              transaction.lastModified = new Date();
              return true;
            }

            if (isNumber(slippageUpdate)) {
              transaction.slippage = slippageUpdate;
            }

            const revision = (transaction.quoteRevision ?? 0) + 1;
            transaction.quoteRevision = revision;
            delete transaction.quote;
            transaction.receiveAmount = transaction.receiveAsset.map(
              () => zeroBalance
            ) as Amount;
            transaction.lastModified = new Date();

            if (transaction.sendAmount[0].mantissa.isZero()) {
              transaction.transactionStatus = TransactionStatus.ZERO_AMOUNT;
              return true;
            }

            transaction.transactionStatus = TransactionStatus.MODIFIED;
            const transactionSnapshot = current(
              transaction as Draft<Transaction>
            ) as Transaction;
            pending = {
              request: createQuoteRequest(
                transactionSnapshot,
                quoteContextRef.current,
                revision
              ),
              transaction: transactionSnapshot,
            };
            latestQuoteRequests.set(component, pending.request);
            return true;
          });
        });
      });

      if (!pending) return updated;
      const pendingQuote = pending;
      let estimatedTransaction: Transaction;
      try {
        const adapter = network.getPoolAdapter(pendingQuote.transaction.poolId);
        if (
          debounceMs &&
          !PoolDataCache.getFor(adapter.poolConfig, network.toolkit)
        )
          await new Promise((resolve) => setTimeout(resolve, debounceMs));
        if (latestQuoteRequests.get(component) !== pendingQuote.request)
          return false;
        if (submitAfterRefresh)
          await adapter.getPoolData(network.toolkit, true);
        estimatedTransaction = await estimateWithAdapter(
          pendingQuote.transaction,
          network.toolkit,
          adapter
        );
      } catch (error) {
        await transactionUpdateMutex.runExclusive(() => {
          setTransactions((draft) => {
            const activeTransaction = draft[component];
            if (
              activeTransaction &&
              latestQuoteRequests.get(component) === pendingQuote.request &&
              quoteRequestMatches(
                current(activeTransaction as Draft<Transaction>) as Transaction,
                quoteContextRef.current,
                pendingQuote.request
              )
            ) {
              activeTransaction.transactionStatus = TransactionStatus.FAILED;
              activeTransaction.lastModified = new Date();
            }
          });
        });
        console.error("Error refreshing transaction quote:", error);
        return false;
      }

      const isLatestRequest =
        latestQuoteRequests.get(component) === pendingQuote.request;
      await transactionUpdateMutex.runExclusive(() => {
        setTransactions((draft) => {
          const activeTransaction = draft[component];
          if (!activeTransaction) return;
          if (latestQuoteRequests.get(component) !== pendingQuote.request) {
            return;
          }

          const transactionSnapshot = current(
            activeTransaction as Draft<Transaction>
          ) as Transaction;
          const balanceStatus = isWalletConnected
            ? checkSufficientBalance(
                transactionSnapshot.sendAssetBalance,
                estimatedTransaction.sendAmount,
                transactionSnapshot.sendAsset
              )
            : TransactionStatus.INITIALIZED;
          const nextStatus =
            submitAfterRefresh &&
            balanceStatus === TransactionStatus.SUFFICIENT_BALANCE
              ? TransactionStatus.PENDING
              : balanceStatus;
          const quotedTransaction = applyQuoteResult(
            transactionSnapshot,
            estimatedTransaction,
            quoteContextRef.current,
            pendingQuote.request,
            nextStatus,
            nextStatus === TransactionStatus.PENDING
          );

          if (quotedTransaction) {
            draft[component] = quotedTransaction as WritableDraft<Transaction>;
          }
        });
      });

      return isLatestRequest;
    },
    [
      checkSufficientBalance,
      isWalletConnected,
      latestQuoteRequests,
      network,
      setTransactions,
      transactionUpdateMutex,
    ]
  );

  const refreshTransactionQuote = useCallback(
    async (
      component: TransactingComponent,
      amountUpdateSend?: Amount,
      slippageUpdate?: number,
      debounceMs = 0
    ): Promise<boolean> =>
      runTransactionQuote(
        component,
        amountUpdateSend,
        slippageUpdate,
        false,
        debounceMs
      ),
    [runTransactionQuote]
  );

  const prepareTransactionForSubmission = useCallback(
    async (component: TransactingComponent): Promise<boolean> =>
      runTransactionQuote(component, undefined, undefined, true),
    [runTransactionQuote]
  );

  const previousQuoteAccountRef = useRef<string | null>(address);
  useEffect(() => {
    if (previousQuoteAccountRef.current === address) return;
    previousQuoteAccountRef.current = address;

    Object.values(TransactingComponent).forEach((component) => {
      const transaction = transactions[component];
      if (!transaction) return;

      if (address) {
        void refreshTransactionQuote(component);
      } else {
        const status = transaction.sendAmount[0].mantissa.isZero()
          ? TransactionStatus.ZERO_AMOUNT
          : TransactionStatus.INITIALIZED;
        void invalidateTransactionQuote(component, status);
      }
    });
  }, [
    address,
    invalidateTransactionQuote,
    refreshTransactionQuote,
    transactions,
  ]);

  // exported callback to update amount of a  single component
  const updateAmount = useCallback(
    async (
      component: TransactingComponent,
      amountUpdateSend?: Amount,
      amountUpdateReceive?: Amount,
      slippageUpdate?: number
    ): Promise<boolean> => {
      let updated = true;
      await transactionUpdateMutex.runExclusive(() => {
        setTransactions((draft) => {
          const wasUpdated = updateTransaction(
            draft[component],
            // update function
            (transaction) => {
              // if receive amount has changed update the transaction
              if (
                amountUpdateReceive &&
                !transaction.receiveAmount[0].decimal.eq(
                  amountUpdateReceive[0].decimal
                )
              ) {
                transaction.receiveAmount = amountUpdateReceive;
                updated = true;
              }
              // if slippage has changed update the transaction
              if (
                isNumber(slippageUpdate) &&
                shouldApplySlippageUpdate(
                  transaction.slippage,
                  slippageUpdate,
                  transaction.transactionStatus
                )
              ) {
                if (isValidSlippage(slippageUpdate)) {
                  transaction.slippage = slippageUpdate;
                  if (isWalletConnected) {
                    transaction.transactionStatus = checkSufficientBalance(
                      transaction.sendAssetBalance as Amount,
                      transaction.sendAmount as Amount,
                      transaction.sendAsset as AssetOrAssetPair
                    );
                  }
                } else {
                  transaction.transactionStatus =
                    TransactionStatus.INVALID_SLIPPAGE;
                }
                updated = true;
              }
              // update the transaction status based on the new balance
              return (
                updateTransactionStatusBasedOnBalance(
                  transaction,
                  amountUpdateSend
                ) || updated
              );
            }
          );
          // if updated, update the last modified date
          const activeTransaction = draft[component];
          if (wasUpdated && activeTransaction) {
            activeTransaction.lastModified = new Date();
            // Any legacy direct amount update invalidates the quote. Quote
            // results are installed only through the revision-checked path.
            activeTransaction.quoteRevision =
              (activeTransaction.quoteRevision ?? 0) + 1;
            latestQuoteRequests.delete(component);
            delete activeTransaction.quote;
          } else {
            updated = false;
          }
        });
      });
      return updated;
    },
    [checkSufficientBalance, setTransactions, isWalletConnected]
  );

  // update the wallet connection state
  useEffect(() => {
    if (client) {
      setIsWalletConnected(true);
    } else {
      setIsWalletConnected(false);
    }
  }, [client]);

  // disconnect wallet
  const disconnect = async () => {
    connectionRevisionRef.current += 1;
    addressRef.current = null;
    activeAccountIdentifierRef.current = null;
    clearWalletDerivedState();
    if (client) {
      await disposeDAppClient(client);
    }
    setClient(null);
    setAddress(null);
  };

  const walletInfo: WalletInfo = {
    client,
    address,
    syncActiveAccount,
    isWalletConnected,
    initialiseTransaction,
    updateStatus,
    updateTransactionBalance,
    updateAmount,
    refreshTransactionQuote,
    prepareTransactionForSubmission,
    invalidateTransactionQuote,
    getActiveTransaction,
    disconnect,
    clearTransaction,
  };

  return (
    <WalletContext.Provider value={walletInfo}>
      {props.children}
    </WalletContext.Provider>
  );
}
